import type { EligibleSubsidy, EnvPathSegment } from "@/features/subventions/types/subvention.types";
import { envelopeName, startOfToday, statusOf } from "@/features/subventions/utils/subventionStatus";

// Group visible subsidies into a forest of feed units, ported verbatim from the locked
// /subventions redesign mockup. A unit is either a standalone card or an envelope; an
// envelope's children may themselves be envelopes, so the tree is N levels deep. Real data is
// at most one envelope level, but the recursion is future-proof and exercised by the synthetic
// env_path fixtures.

export type EnvNode = {
  type: "env"
  env: string
  id: string | number
  // The entry whose immediate parent IS this envelope — the source of its shared budget figure.
  // Set once (the first direct child leaf), so it is readonly.
  readonly first: EligibleSubsidy | null
  hasBudget: boolean
  children: FeedNode[]
};

// While assembling the tree, an envelope is a bare frame whose children accumulate as leaves
// arrive; collapse() finalises it into an EnvNode with `first`/`hasBudget` derived from children.
type EnvFrame = {
  type: "env"
  env: string
  id: string | number
  children: BuildNode[]
};
type BuildNode = EnvFrame | SoloNode;

export type SoloNode = {
  type: "solo"
  subsidy: EligibleSubsidy
};

export type FeedNode = EnvNode | SoloNode;

// The visible chain of named ancestors (outer → immediate parent), excluding the hidden V1 root.
// Prefers the de-flattened env_path the V2 adapter emits; falls back to the denormalised
// subsidy_global_* (legacy payloads, or before the API ships env_path), with V1 filtered out.
const pathOf = (subsidy: EligibleSubsidy): EnvPathSegment[] => {
  if (subsidy.env_path?.length) return subsidy.env_path;
  const env = envelopeName(subsidy);
  return env ? [{ id: subsidy.subsidy_global_id, name: env }] : [];
};

// Finalise the build frames into the public tree: derive each envelope's `first` (its first
// direct child leaf) and `hasBudget`, recursing leaves-up. A single-child envelope with no shared
// budget is noise, so collapse it back to a plain card.
const collapse = (nodes: BuildNode[]): FeedNode[] =>
  nodes.map((node) => {
    if (node.type !== "env") return node;
    const children = collapse(node.children);
    const first = node.children.find((child) => child.type === "solo")?.subsidy ?? null;
    // Treat a legacy payload that OMITS global_remaining_amount the same as an explicit null (no
    // shared budget), so both absent (undefined) and null read as "no budget", never a budget.
    const budget = first?.global_remaining_amount;
    const hasBudget = budget !== null && budget !== undefined;
    const collapsed: EnvNode = {
      type: "env", env: node.env, id: node.id, first, hasBudget, children,
    };
    return collapsed.children.length === 1 && !collapsed.hasBudget ? collapsed.children[0] : collapsed;
  });

export const buildFeedTree = (list: EligibleSubsidy[]): FeedNode[] => {
  // Depth-N dedup: a container node also surfaced as a leaf under its grandparent is dropped
  // (its envelope already shows it). Every ancestor segment of a leaf's path is a container, so
  // collect them all — this catches a mid-level envelope, not just the outermost root. Keyed on
  // id as a string (so "SPL_…" and numeric ids never collide) and never on the display name, so
  // two unrelated perimeters that share a name cannot be confused for parent and child.
  const parentIds = new Set<string>();
  list.forEach((subsidy) => {
    pathOf(subsidy).forEach((segment) => parentIds.add(String(segment.id)));
  });

  const roots: BuildNode[] = [];
  const index = new Map<string, EnvFrame>();

  const ensureEnv = (path: EnvPathSegment[]): EnvFrame => {
    const key = path.map((segment) => String(segment.id)).join("/");
    const existing = index.get(key);
    if (existing) return existing;
    const segment = path[path.length - 1];
    const node: EnvFrame = {
      type: "env", env: segment.name, id: segment.id, children: [],
    };
    index.set(key, node);
    if (path.length === 1) roots.push(node);
    else ensureEnv(path.slice(0, -1)).children.push(node);
    return node;
  };

  list.forEach((subsidy) => {
    if (parentIds.has(String(subsidy.subsidy_perimeter_id))) return;
    const path = pathOf(subsidy);
    if (!path.length) {
      roots.push({ type: "solo", subsidy });
      return;
    }
    ensureEnv(path).children.push({ type: "solo", subsidy });
  });

  return collapse(roots);
};

// Every leaf entry in a node's subtree.
export const leafSubs = (node: FeedNode): EligibleSubsidy[] =>
  node.type === "solo" ? [node.subsidy] : node.children.flatMap(leafSubs);

// A unit's deadline drives its tier; an envelope takes the EARLIEST among its descendants.
export const unitDate = (node: FeedNode, now: Date = startOfToday()): Date | null => {
  const times = leafSubs(node)
    .map((subsidy) => statusOf(subsidy, now).date)
    .filter((date): date is Date => date !== null)
    .map((date) => date.getTime());
  return times.length ? new Date(Math.min(...times)) : null;
};

// A unit is depleted when nothing in it is still spendable, so it can sink to the foot of its tier.
const unitDepleted = (node: FeedNode, now: Date = startOfToday()): boolean =>
  leafSubs(node).every((subsidy) => statusOf(subsidy, now).tone === "depleted");

// Order sibling nodes: spent ones sink, then earliest deadline first. Used at the top level and
// inside every envelope.
export const sortNodes = (nodes: FeedNode[], now: Date = startOfToday()): FeedNode[] =>
  nodes.slice().sort((x, y) => {
    const xDepleted = unitDepleted(x, now) ? 1 : 0;
    const yDepleted = unitDepleted(y, now) ? 1 : 0;
    if (xDepleted !== yDepleted) return xDepleted - yDepleted;
    const xDate = unitDate(x, now);
    const yDate = unitDate(y, now);
    return (xDate ? xDate.getTime() : Infinity) - (yDate ? yDate.getTime() : Infinity);
  });
